import classNames from "classnames";
import { useMemo } from "react";

import { CaseDocumentWithPresignedUrl, Case } from "pwapi";

import {
  openDocument,
  updateCaseDocument,
} from "shared/actions/caseDetailActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { timestampToLocaleDateString } from "shared/utils/dates";

import { canAttachDocument } from "components/admin/documentsPanel/editDocumentsFormUtils";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { useAppDispatch, useAppSelector } from "store/hooks";

import { isModifyingReport } from "./dialogs/utils";

const DocsListItem = ({
  caseDetail,
  document,
  setOpenPreview,
}: {
  caseDetail: CaseDetailState;
  document: CaseDocumentWithPresignedUrl;
  setOpenPreview?: Function;
}) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);
  const features = useAppSelector((state) => state.sys.features);
  const useMergeCaseReportWorkflow = useAppSelector((state) =>
    selectFeatureFlag(state, "useMergeCaseReportWorkflow")
  );

  const openDoc = (id: number) => {
    caseDetail.id && dispatch(openDocument(caseDetail.id, id));
  };

  const isDeadDoc = caseDetail?.deadDocIds.indexOf(document.id || 0) !== -1;

  const enableAppendDocument = useMemo(() => {
    //Special edge case, Finalized cases will only enable this feature if the case is in an amend, addend, or report correction state
    //Superusers are free to do as they need similar to the administration pages
    if (caseDetail.status === Case.status.FINALIZED) {
      return (
        !!isModifyingReport(caseDetail, features, currentUser)?.source ||
        currentUser?.is_superuser
      );
    }

    //If the case is active or working, the control will be enabled, the control's visibility is a different check.
    if (
      caseDetail.status === Case.status.ACTIVE ||
      caseDetail.status === Case.status.WORKING
    )
      return true;

    //If the case is in any other status but those three, the button should not be enabled
    return false;
  }, [caseDetail, currentUser]);

  const updateDocument = () => {
    dispatch(
      updateCaseDocument({
        parentLookupCaseId: caseDetail.id || 0,
        id: document.id || 0,
        requestBody: {
          attach_to_case_report: !document.attach_to_case_report,
        } as CaseDocumentWithPresignedUrl,
      })
    );
  };

  return (
    <li
      className={classNames("DocumentListItem", {
        "DocumentListItem--appended": document.attach_to_case_report,
      })}
    >
      <div className="DocumentListItem__section--vertical">
        <button
          type="button"
          className={classNames(
            "DocumentListItem__section",
            "DocumentListItem__section--horizontal",
            "DocumentListItem__section--spread",
            {
              "DocumentListItem--arrowCursor": !openDocument,
              "DocumentListItem--pointer": openDocument,
            }
          )}
          onClick={() => document?.id && !isDeadDoc && openDoc(document.id)}
          disabled={isDeadDoc}
        >
          {document.wpl_document_metadata &&
          Object.keys(document.wpl_document_metadata)?.length ? (
            <strong>
              {document.wpl_document_metadata.type}
              {document.wpl_document_metadata.type !==
                document.wpl_document_metadata.comment &&
                document.wpl_document_metadata.comment && (
                  <> {document.wpl_document_metadata.comment}</>
                )}
            </strong>
          ) : (
            <strong>{document.name?.replace(".pdf", "")}</strong>
          )}
          {document.created_at && (
            <span className="DocumentListItem--lightText">
              {timestampToLocaleDateString(document.created_at)}
            </span>
          )}
        </button>
        <div className="DocumentListItem__section--horizontal DocumentListItem__section--spread DocumentListItem--lightText">
          <span>{document.category}</span>
          {!document.wpl_document_metadata?.length &&
            useMergeCaseReportWorkflow &&
            !caseDetail.is_screening &&
            canAttachDocument(document.category) &&
            (caseDetail.active_assignment?.id === currentUser?.id ||
              currentUser?.is_superuser) && (
              <label
                className={classNames(
                  "DocumentListItem__section--horizontal",
                  "DocumentListItem--pointer",
                  {
                    "DocumentListItem--lightText": isDeadDoc,
                  }
                )}
                style={{ gap: "0.5em" }}
              >
                <input
                  type="checkbox"
                  checked={!!document.attach_to_case_report}
                  disabled={isDeadDoc || !enableAppendDocument}
                  onChange={updateDocument}
                />
                Attach to Report
              </label>
            )}
        </div>
      </div>
      {setOpenPreview && (
        <PathologyWatchButton
          size="small"
          onClick={() => setOpenPreview(document.id)}
          disabled={isDeadDoc}
        >
          Preview
        </PathologyWatchButton>
      )}
    </li>
  );
};

export default DocsListItem;
